import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Text, Pane, Flex, Label, Selector, Toast, Content, Button } from '@leverege/ui-elements';
import { LoadingView, UseResponsiveBreakpoints } from '@leverege/molten/views';
import P from '@leverege/promise';
import { DataSources } from '@leverege/ui-attributes';
import { GlobalState, Selection } from '@leverege/ui-redux';
import { TitleBar } from '@leverege/ui-plugin';
import { getLocations } from '../../util/Interface';
import Loading from '../../components/Loading';
import S from './VehicleTransferLocation.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function VehicleTransferLocation(props) {
  const {
    show,
    onClose,
    typeUpperCase,
    type,
    parentItem,
    targets,
    reloadData,
    dispatch,
    targetKey,
    mobile
  } = props;
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [keysEnabled, setKeysEnabled] = useState(null);
  const [locator, setLocator] = useState();
  const [loading, setLoading] = useState(true);
  const [keysAndTags, setKeysAndTags] = useState([]);
  const getKeyTag = useCallback(async (items, vehicleActions) => {
    const newKeysAndTags = [];
    const systemId = parentItem?.data?.systemId;
    const companyId = parentItem.type === 'company' ? parentItem.id : parentItem?.data?.data?.company?.id;
    const vehicleDevice = await GlobalState.dispatch(vehicleActions.get());
    const locationId = vehicleDevice.data?.location?.id;
    const loc = {
      id: locationId,
      type: 'location',
      ref: `${systemId}/company/${companyId}/locations`
    };
    const locationActions = DataSources.getActions(loc);
    try {
      await P.mapLimit(items, async key => {
        const tagId = key.data?.tag?.id;
        try {
          const tag = await GlobalState.dispatch(locationActions.child('tags').itemActions(tagId).get());
          newKeysAndTags.push({
            key: key.name,
            tag: tag.name
          });
        } catch (err) {
          console.log('Could not find tag paired to key', err);
          newKeysAndTags.push({
            key: key.name
          });
        }
      }, {
        limit: 5
      });
    } catch (err) {
      console.error('Error fetching key tags', err);
    }
    setKeysAndTags(newKeysAndTags);
  }, []);
  const getVehicleKeysAndTag = useCallback(async vehicle => {
    const vehicleActions = DataSources.getDelegate(vehicle);
    const {
      items
    } = await GlobalState.dispatch(vehicleActions.child('keys').list());
    await getKeyTag(items, vehicleActions);
  }, [getKeyTag]);
  const getTargetLocation = async targets => {
    let isKeysEnabled = false;
    // check if we are at location or company level and get location data accordingly
    if (parentItem.type === 'location') {
      isKeysEnabled = parentItem.data.data.keysEnabled;
      // else at the company level and need to check each location if they have at least 1 keys enabled location to determine dialog
    } else {
      const systemId = parentItem?.data?.systemId;
      const companyId = parentItem.type === 'company' ? parentItem.id : parentItem?.data?.data?.company?.id;
      // get location from vehicle selected since parentItem isnt reliable at all levels
      try {
        await P.mapLimit(targets, async target => {
          const targetActions = DataSources.getDelegate(target);
          const targetDevice = await GlobalState.dispatch(targetActions.get());
          const locationId = targetDevice.data?.location?.id;
          const loc = {
            id: locationId,
            type: 'location',
            ref: `${systemId}/company/${companyId}/locations`
          };
          const locationActions = DataSources.getActions(loc);
          const locationDevice = await GlobalState.dispatch(locationActions.get());
          const keysEnabled = locationDevice.data?.keysEnabled;
          if (keysEnabled) {
            isKeysEnabled = true;
          }
        }, {
          limit: 5
        });
      } catch (err) {
        console.error('Could not get location', err);
      }
    }
    setKeysEnabled(isKeysEnabled);
    return isKeysEnabled;
  };
  const getLocator = async target => {
    let locatorName;
    if (target.type === 'locator') {
      locatorName = target.data.name;
    } else {
      locatorName = target.data?.data?.locator?.data?.serialNumber;
    }
    setLocator(locatorName);
  };
  useEffect(() => {
    const systemId = parentItem?.data?.systemId;
    const companyId = parentItem.type === 'company' ? parentItem.id : parentItem?.data?.data?.company?.id;
    getLocations(systemId, companyId).then(res => {
      const filteredLocations = res.filter(item => parentItem.type === 'company' ? true : item.id !== parentItem.id);
      setLocations(filteredLocations);
    });
    // run all data fetching calls and await it before rendering components
    const fetchData = async () => {
      try {
        const getTargetLocationPromise = getTargetLocation(targets);
        const promisesToAwait = [getTargetLocationPromise];

        // continue fetching the rest of the data if the target has keys enabled AND only one target is selected
        if (getTargetLocationPromise && targets.length === 1) {
          promisesToAwait.push(getVehicleKeysAndTag(targets[0]), targets[0].type === 'vehicle' && getLocator(targets[0]));
        }
        await Promise.all(promisesToAwait);
        setLoading(false);
      } catch (err) {
        console.log('Error fetching all data', err);
        setLoading(false);
      }
    };
    fetchData();
  }, [targets]);
  const onDropdownChange = async evt => {
    setLocationId(evt.value);
  };
  const onOkay = async () => {
    const systemId = parentItem?.data?.systemId;
    const companyId = parentItem.type === 'company' ? parentItem.id : parentItem?.data?.data?.company?.id;
    const closeLoading = LoadingView.showDialog(targets.length > 1 ? `Transferring ${typeUpperCase}s` : `Transferring ${typeUpperCase}`);

    // TODO: This will break if the ref of the parentItem is ever fixed in molten
    const loc = {
      id: locationId,
      type: 'location',
      ref: `${systemId}/company/${companyId}/locations`
    };
    try {
      const locationActions = DataSources.getActions(loc);
      // move targets
      await P.mapLimit(targets, target => {
        return GlobalState.dispatch(locationActions.child(`${type}s`).create({
          id: target.id,
          force: true
        }));
      }, {
        handleReject: false,
        limit: 2
      });
      await reloadData();
      Toast.success(`Successfully transferred ${type}s`);
      dispatch(Selection.remove(targetKey, targets));
    } catch (e) {
      console.error(e);
      Toast.error([`Error transferring ${type}s`]);
    }
    onClose();
    closeLoading();
  };

  // sets loading screen to wait for all data fetching to finish before rendering components 
  if (loading) {
    return /*#__PURE__*/_jsx(Loading, {
      loading: true
    });
  }

  // check that at least one paired device exists before showing the paired devices component
  // const atLeastOnePairedItem = locator || keys.length !== 0 || tags.length !== 0
  const atLeastOnePairedItem = locator || keysAndTags.length !== 0;

  // turn array of keys and tags into list of keys and tags for rendering
  const renderedKeysAndTags = keysAndTags.map(keyAndTag => /*#__PURE__*/_jsx("li", {
    children: /*#__PURE__*/_jsx(Text, {
      children: `${keyAndTag.key} ${keyAndTag.tag ? `/ ${keyAndTag.tag}` : ''}`
    })
  }, `${keyAndTag.key}-${keyAndTag.tag}`));

  // if user selects only one device and keys is enabled, show paired devices if vehicle has any
  let body;
  if (targets.length === 1) {
    if (keysEnabled && targets[0].type !== 'locator') {
      body = /*#__PURE__*/_jsx(Flex, {
        variant: "colL",
        children: atLeastOnePairedItem && /*#__PURE__*/_jsxs(Pane, {
          children: [/*#__PURE__*/_jsxs(Text, {
            children: ["The following paired ", locator && keysAndTags.length > 0 || keysAndTags.length > 1 ? 'devices' : 'device', " will also be transferred:"]
          }), locator && /*#__PURE__*/_jsxs(Flex, {
            variant: "colS",
            className: S.container,
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "sectionTitle",
              children: "Locator: "
            }), /*#__PURE__*/_jsx(Text, {
              children: locator
            })]
          }), keysAndTags.length !== 0 && /*#__PURE__*/_jsxs(Flex, {
            variant: "colS",
            className: S.container,
            children: [/*#__PURE__*/_jsx(Label, {
              variant: "sectionTitle",
              children: "Paired Keys & Tags: "
            }), /*#__PURE__*/_jsx("ul", {
              className: S.devicesList,
              children: renderedKeysAndTags
            })]
          })]
        })
      });
    } else {
      // user selects one but location, does not have keys enabled or is a locator
      body = locator && /*#__PURE__*/_jsx(Flex, {
        variant: "colL",
        children: /*#__PURE__*/_jsxs(Pane, {
          children: [/*#__PURE__*/_jsx(Text, {
            style: {
              fontWeight: 'bold'
            },
            children: "The following paired locator will also be transferred:"
          }), /*#__PURE__*/_jsx("div", {
            className: S.container,
            children: /*#__PURE__*/_jsx(Flex, {
              variant: "colS",
              children: locator && /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(Label, {
                  variant: "sectionTitle",
                  children: "Locator: "
                }), locator]
              })
            })
          })]
        })
      });
    }
    // if user selects multiple devices and keys is enabled or not
  } else {
    body = /*#__PURE__*/_jsx(Text, {
      children: /*#__PURE__*/_jsxs("b", {
        children: ["If ", targets.length > 1 ? 'any' : 'the', " ", type, " is paired", type === 'locator' ? ', the Vehicle' : keysEnabled ? ', any Locators or Keys' : ', the Locator', " will also be transferred."]
      })
    });
  }

  // set header, content, and footer of dialog popup
  const header = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TitleBar, {
      variant: "dialogTitle",
      title: "Transfer to New Location",
      icon: "https://recovr-ui-assets.storage.googleapis.com/location_details.png"
    }), /*#__PURE__*/_jsx(Button, {
      variant: "closeDialog",
      className: S.close,
      onClick: onClose
    })]
  });
  const content = /*#__PURE__*/_jsxs(Pane, {
    layout: "flex:colM",
    children: [/*#__PURE__*/_jsxs(Text, {
      children: [targets.length > 1 ? 'These' : 'This', " ", type === 'locator' ? targets.length > 1 ? 'locators' : 'locator' : targets.length > 1 ? 'vehicles' : 'vehicle', " will be transferred to the new location selected below."]
    }), /*#__PURE__*/_jsx(Label, {
      variant: "inputTitle",
      children: "New Location"
    }), /*#__PURE__*/_jsx(Selector, {
      value: locationId,
      targetAttachment: "top right",
      valueKey: "id",
      onChange: onDropdownChange,
      values: locations
    })]
  });
  const footer = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {
      onClick: onClose,
      variant: "secondary",
      children: "Cancel"
    }), /*#__PURE__*/_jsx(Button, {
      "data-test-id": "button.pairing.pair",
      variant: "primary",
      onClick: onOkay,
      disabled: !locationId,
      children: targets.length > 1 ? `Transfer ${typeUpperCase}s` : `Transfer ${typeUpperCase}`
    })]
  });
  return /*#__PURE__*/_jsx(Dialog, {
    className: S.dialog,
    show: show,
    closeOnEscape: true,
    closeOnDocumentClick: true,
    onClose: onClose,
    position: mobile ? 'full' : 'center',
    children: /*#__PURE__*/_jsxs(Content, {
      relative: !mobile,
      children: [header && /*#__PURE__*/_jsx(Content.Header, {
        variant: "itemFormHeader|formHeader",
        className: S.header,
        children: header
      }), content && /*#__PURE__*/_jsx(Content.Area, {
        variant: "itemFormBody|formBody",
        className: S.form,
        children: /*#__PURE__*/_jsxs(Flex, {
          variant: "colM",
          children: [content, atLeastOnePairedItem && /*#__PURE__*/_jsx("hr", {
            className: S.bar
          }), body]
        })
      }), footer && /*#__PURE__*/_jsx(Content.Footer, {
        variant: "formButtons",
        layout: "flex:rowMEnd",
        children: footer
      })]
    })
  });
}

// sets mobile prop for desktop or mobile viewing
function VehicleTransferLocationMobile(props) {
  return /*#__PURE__*/_jsx(VehicleTransferLocation, {
    ...props,
    mobile: true
  });
}
function VehicleTransferLocationDesktop(props) {
  return /*#__PURE__*/_jsx(VehicleTransferLocation, {
    ...props,
    mobile: false
  });
}
const components = {
  small: VehicleTransferLocationMobile,
  big: VehicleTransferLocationDesktop
};
export default function VehicleTransferLocationResponsive(props) {
  return UseResponsiveBreakpoints(components, props);
}